/**
 * The one `documents.status` value that means the rag-api ingest pipeline
 * finished: content cleaned, chunked, enriched AND embedded.
 */
export const DOC_STATUS_INDEXED = 'completed';
/**
 * Whether the assistant can actually retrieve this document.
 *
 * Mirrors `_is_retrievable` in the orchestrator's `tkb/summary.py`, which
 * decides the same thing for the prompt's topics block. Both conditions matter
 * and they catch different failures:
 *
 * - `status === 'completed'` — the pipeline reached the end. A `failed`
 *   document still appears in this list, because the API does not filter on
 *   status, and it still reports a full `chunk_count`, because chunks are
 *   inserted BEFORE enrichment runs. Chunk count alone cannot tell them apart.
 * - `chunk_count > 0` — a document can legitimately complete with zero chunks,
 *   and there is nothing to retrieve from it either.
 *
 * Fail-open on an ABSENT status, which is the opposite of the orchestrator's
 * choice, deliberately: there, a document that cannot prove it is indexed must
 * not be advertised to the model. Here, an unexplained badge on every row of
 * an older API response would be worse than no badge — this surface informs,
 * it does not gate.
 */
export function isDocumentIndexed(doc) {
    var _a;
    if (doc.status === undefined || doc.status === '')
        return true;
    if (doc.status !== DOC_STATUS_INDEXED)
        return false;
    return ((_a = doc.chunk_count) !== null && _a !== void 0 ? _a : 0) > 0;
}
/** Statuses where the pipeline is genuinely still running. */
const DOC_STATUSES_IN_FLIGHT = new Set(['pending', 'processing']);
/**
 * Whether a not-yet-indexed document is still working on it.
 *
 * Deliberately NOT `!== 'failed'`. A `completed` document with zero chunks is
 * also unsearchable, and ingest has already finished for it — labelling that
 * "Indexing" would promise progress that will never arrive. Only the genuinely
 * in-flight statuses get the optimistic label; everything else that fails
 * `isDocumentIndexed` is simply not searchable.
 */
export function isDocumentIndexing(doc) {
    var _a;
    if (isDocumentIndexed(doc))
        return false;
    return DOC_STATUSES_IN_FLIGHT.has((_a = doc.status) !== null && _a !== void 0 ? _a : '');
}
